import { siteConfig } from "./productData";

const brand = siteConfig.brandName;

export const policyPages = [
  {
    slug: "legal-notice",
    title: "Legal notice",
    summary: "Who operates this storefront, how to send formal notices and how third-party rights are treated.",
    highlight: "This is an independent poodliving storefront. No affiliation with another retailer, marketplace or brand is implied.",
    sections: [
      {
        title: "Storefront identification",
        paragraphs: [
          `${brand} is the trading name displayed on this online store. The verified business address is ${siteConfig.contact.address}, the operating country is ${siteConfig.operatingCountry}, and the customer-service and legal-notice email is ${siteConfig.contact.email}.`,
          "The details published here are limited to verified information from the store operator; no unverified company number, tax number or telephone number is asserted. Transaction-specific seller information required by applicable law may be requested through the contact channel above.",
        ],
      },
      {
        title: "Independent operation",
        paragraphs: [
          `${brand} is not presented as an agent, branch, franchisee or authorised representative of any other online retailer. References to third-party products, services or payment methods are descriptive only and do not imply sponsorship or endorsement.`,
          "Third-party trademarks, product names and logos remain the property of their respective owners.",
        ],
      },
      {
        title: "Formal notices",
        paragraphs: [
          `Send legal, consumer or rights-related notices to ${siteConfig.contact.email} with “Legal notice” in the subject line. Include your full name, a reliable reply address, the relevant order or page, and a clear description of the request.`,
          "Email delivery does not by itself prove legal service. If a particular law or contract requires another method, use that method as well.",
        ],
      },
      {
        title: "Accuracy and updates",
        paragraphs: [
          "We aim to keep product, price and policy information understandable and current, but typographical or technical errors can occur. Material order information should be checked before payment.",
          "This notice may be updated when the operator details or services change. Changes do not retrospectively remove rights that already apply to an accepted order.",
        ],
      },
    ],
    related: ["terms", "intellectual-property", "complaints"],
  },
  {
    slug: "payments",
    title: "Payment policy",
    summary: "Payment authorisation, currency, security, failed payments, refunds and charge questions.",
    highlight: "Card and wallet credentials are entered on the hosted payment provider’s service, not into the poodliving address form.",
    sections: [
      {
        title: "Available payment methods",
        paragraphs: [
          "The methods actually available are the methods displayed by the hosted payment page for your device and location. A logo shown elsewhere on the site is informational and does not guarantee that a particular method will be offered for every order.",
          "You must be authorised to use the selected payment method and provide accurate billing and authentication information.",
        ],
      },
      {
        title: "Authorisation and capture",
        paragraphs: [
          "A bank or wallet provider may place an authorisation hold before a payment is finally captured. An order is not accepted merely because an authorisation appears on an account.",
          "Payment can be refused or reversed by the provider, card issuer or us where authentication fails, information is inconsistent, fraud is suspected or the order cannot be accepted.",
        ],
      },
      {
        title: "Currency, tax and total",
        paragraphs: [
          `Store prices and checkout totals are shown in ${siteConfig.currency}. Delivery and any other charge applied by the store are shown before payment.`,
          "Your payment provider may apply its own conversion rate or foreign-transaction fee. Customs duties, import taxes or carrier collection fees imposed outside the checkout are governed by the destination rules and are not payment-provider charges from us.",
        ],
      },
      {
        title: "Failed, pending or duplicate charges",
        paragraphs: [
          "Do not repeatedly submit payment while a result is pending. First check the payment result, your email and the cart. Multiple pending entries may be temporary authorisations rather than completed charges.",
          `If two completed charges appear for the same order, contact ${siteConfig.contact.email} with the order reference, dates, amounts and only the last four digits of the payment card. Never email a complete card number or security code.`,
        ],
      },
      {
        title: "Refunds and disputes",
        paragraphs: [
          "Approved refunds are requested to the original payment method. Posting time is controlled by the payment provider or bank. Refunds are limited to the amount actually paid and may reflect returned items, discounts, delivery rules and any lawful value deduction.",
          "Please contact us before opening a payment dispute so we can investigate. This request does not limit any right to contact your bank or use a statutory remedy.",
        ],
      },
    ],
    related: ["terms", "orders", "returns"],
  },
  {
    slug: "orders",
    title: "Order & cancellation policy",
    summary: "How an order is submitted, reviewed, accepted, changed or cancelled.",
    highlight: "Checkout submission is an offer to buy; acceptance occurs only as described in the terms and order communications.",
    sections: [
      {
        title: "Before submitting",
        paragraphs: [
          "Review the product, variant, quantity, price, delivery country, address and email before continuing to payment. Customers must be able to enter into the purchase and use an authorised payment method.",
          "Items left in a cart are not reserved, and availability or price can change before an order is accepted.",
        ],
      },
      {
        title: "Order review and acceptance",
        paragraphs: [
          "An automated receipt or payment result confirms that information was received; it does not necessarily mean the order has been accepted. We may review stock, address validity, payment status and misuse indicators.",
          "If an order cannot be accepted, any captured amount should be returned to the original method. A legitimate rejection may result from unavailable stock, an obvious error, delivery restrictions, failed verification or suspected unlawful activity.",
        ],
      },
      {
        title: "Changes and cancellation requests",
        paragraphs: [
          `Send a change or cancellation request promptly to ${siteConfig.contact.email} with the order reference. We will try to act before fulfilment begins, but a change is not guaranteed after payment or packing.`,
          "If dispatch can no longer be stopped, the returns process may apply instead. Non-excludable cancellation or withdrawal rights remain available where applicable.",
        ],
      },
      {
        title: "Unavailable items and substitutions",
        paragraphs: [
          "We do not silently replace an unavailable item with a materially different product. We may offer a clear alternative, partial fulfilment or cancellation and refund, and the customer can choose where a choice is legally required.",
          "A minor packaging update that does not change the ordered product’s essential characteristics is not treated as an unauthorised substitution.",
        ],
      },
      {
        title: "Order limits and misuse",
        paragraphs: [
          "Reasonable quantity or household limits may be applied to scarce, promotional or safety-sensitive products. Orders associated with resale abuse, payment fraud, sanctions violations or interference with the store may be refused or cancelled where lawful.",
          "We will not cancel an order for a prohibited discriminatory reason.",
        ],
      },
    ],
    related: ["terms", "payments", "shipping"],
  },
  {
    slug: "warranty-claims",
    title: "Warranty & claims policy",
    summary: "How to report faulty, damaged, incorrect or misdescribed products and the available review process.",
    highlight: "Where EU rules apply, new goods carry a minimum two-year legal guarantee; this policy never replaces mandatory consumer rights.",
    sections: [
      {
        title: "What this policy covers",
        paragraphs: [
          "Contact us if an item arrives damaged, is materially different from its description, is the wrong item, or develops a fault that is not reasonably attributable to normal wear or misuse. Where EU consumer rules apply, new goods have a minimum two-year legal guarantee from delivery.",
          "Any manufacturer warranty is additional to rights against the seller unless the applicable law says otherwise.",
        ],
      },
      {
        title: "Making a claim",
        paragraphs: [
          `Email ${siteConfig.contact.email} with the order reference, product name, date the issue was noticed, a clear explanation and useful photographs or video. Do not include unnecessary personal data.`,
          "Keep the item and packaging until instructions are provided. Do not send unsafe, leaking or restricted materials through ordinary post without approval from the carrier and us.",
        ],
      },
      {
        title: "Assessment",
        paragraphs: [
          "We may ask reasonable troubleshooting questions or arrange an inspection. Assessment considers the product description, intended use, care instructions, delivery condition and the nature and timing of the reported issue.",
          "Proof of purchase can be an order confirmation, payment record or other reliable evidence; a specific paper receipt is not required where the purchase can otherwise be verified.",
        ],
      },
      {
        title: "Possible remedies",
        paragraphs: [
          "Depending on the issue and applicable law, a remedy may be repair, replacement, missing parts, price reduction, refund or another agreed solution. Under EU rules, repair or replacement is normally provided without charge, with a price reduction or refund available where the relevant conditions are met. We cover reasonable return costs where the law or an accepted valid claim requires it.",
          "No remedy will unlawfully reduce a mandatory right. A refund is normally sent to the original payment method.",
        ],
      },
      {
        title: "What is not a product fault",
        paragraphs: [
          "Normal depletion, expected wear, accidental damage, improper storage, use contrary to instructions, unauthorised modification and reasonable screen-to-product colour variation are not automatically defects.",
          "These exclusions do not apply where the stated cause is itself the result of a defect or where applicable law provides otherwise.",
        ],
      },
    ],
    related: ["returns", "product-safety", "complaints"],
  },
  {
    slug: "product-safety",
    title: "Product safety & care policy",
    summary: "Safe selection, age guidance, art-material handling, storage, disposal and incident reporting.",
    highlight: "Read the label and product instructions before use; age guidance does not replace appropriate adult supervision.",
    sections: [
      {
        title: "Choose and use products carefully",
        paragraphs: [
          "Use products only for their intended creative, writing, office or storage purpose and follow all warnings, labels and instructions supplied with the item. Product-page summaries do not replace the manufacturer’s safety information.",
          "Check size, materials and age guidance before purchase, especially where allergies, sensitivities or accessibility needs are relevant.",
        ],
      },
      {
        title: "Children and vulnerable users",
        paragraphs: [
          "Products are purchased by adults. Small parts, sharp tools, inks, paints, adhesives and other art materials may require close adult supervision and may be unsuitable for young children even when used for a creative activity.",
          "Keep packaging, blades, cords, caps and non-food materials away from children who may swallow or misuse them.",
        ],
      },
      {
        title: "Art materials and tools",
        paragraphs: [
          "Do not ingest art materials or use them on skin, food-contact surfaces or toys unless the product is expressly labelled for that use. Work in suitable ventilation, protect surfaces and wash hands after use where the label recommends it.",
          "Cutting tools, sharpeners, compasses and similar products should be handled, carried and stored with protective parts in place.",
        ],
      },
      {
        title: "Storage and disposal",
        paragraphs: [
          "Store products closed, upright where appropriate, away from heat, flame, moisture, pets and unsupervised children. Dispose of liquids, batteries, blades and chemical products through a suitable local collection route rather than a drain or ordinary waste where prohibited.",
          "Do not use a product whose packaging is leaking, swollen or materially damaged.",
        ],
      },
      {
        title: "Incidents, warnings and recalls",
        paragraphs: [
          `Stop using a product and contact ${siteConfig.contact.email} if you believe it presents an unexpected safety risk. Include the order reference, product, batch or label details and photographs where safe to do so.`,
          "For an urgent medical situation, fire or poisoning concern, contact the appropriate local emergency or poison-information service first. We may contact customers about a confirmed safety notice using order contact details.",
        ],
      },
    ],
    related: ["children", "warranty-claims", "shipping"],
  },
  {
    slug: "promotions",
    title: "Promotions & pricing policy",
    summary: "Rules for discounts, promotional codes, limited offers, pricing errors and promotional returns.",
    highlight: "The cart shows the applied discount before payment; a code advertised elsewhere is not valid unless checkout accepts it.",
    sections: [
      {
        title: "Offer terms",
        paragraphs: [
          "Each promotion is subject to its displayed dates, eligible products, market, minimum spend, quantity limit and any customer-specific restriction. Unless stated otherwise, offers cannot be exchanged for cash or applied after an order is placed.",
          "Only one code may be used per order unless checkout expressly permits stacking.",
        ],
      },
      {
        title: "Availability and changes",
        paragraphs: [
          "Limited-stock offers may end when the allocated inventory is exhausted. We may correct or withdraw an offer before an order is accepted if it contains an obvious error or is being exploited contrary to its stated purpose.",
          "We will not retrospectively increase the accepted price of an order because a promotion later ends.",
        ],
      },
      {
        title: "Discount allocation and returns",
        paragraphs: [
          "An order-level discount may be allocated proportionally across eligible items. A refund for a returned item reflects the amount paid for that item after the allocated discount.",
          "If a return causes the retained order to fall below an offer threshold, any adjustment will be made only where the disclosed offer terms and applicable law permit it.",
        ],
      },
      {
        title: "Fair use",
        paragraphs: [
          "Codes must not be generated, guessed, sold, published in breach of a confidentiality restriction or used through automated orders. We may reject duplicated or manipulated redemptions while preserving legitimate consumer rights.",
          "A promotion does not create an entitlement to stock that is unavailable or to a price that a reasonable customer would recognise as an error.",
        ],
      },
    ],
    related: ["terms", "orders", "payments"],
  },
  {
    slug: "acceptable-use",
    title: "Acceptable use policy",
    summary: "Rules that protect customers, the storefront and service providers from misuse.",
    highlight: "Use the store for genuine browsing, purchasing and customer-service activity without disrupting other visitors or violating the law.",
    sections: [
      {
        title: "Permitted use",
        paragraphs: [
          "You may browse the public catalogue, use ordinary shopping features, place legitimate orders and contact customer service for lawful personal or business purposes consistent with these policies.",
          "Reasonable sharing of a public product link is permitted. Commercial reuse of site content is governed by the intellectual-property policy.",
        ],
      },
      {
        title: "Prohibited conduct",
        paragraphs: [
          "Do not attempt fraud, impersonation, unauthorised access, payment testing, malicious code delivery, denial of service, data harvesting, deceptive returns, sanctions evasion or any activity that infringes another person’s rights.",
          "Do not submit unlawful, threatening, discriminatory, sexually exploitative, privacy-invasive or infringing material through a form or email channel connected to the store.",
        ],
      },
      {
        title: "Automated access",
        paragraphs: [
          "Do not use bots, scrapers or other automated means in a way that burdens the service, bypasses controls, creates orders or extracts protected content. Search engines may index public pages in accordance with the site’s technical instructions.",
          "Written permission is required for any higher-volume or commercial data access.",
        ],
      },
      {
        title: "Enforcement",
        paragraphs: [
          "We may rate-limit, block, cancel or investigate activity reasonably believed to violate this policy, and may preserve or disclose relevant records where required by law. Action will be proportionate to the risk where practical.",
          "Nothing in this policy prevents good-faith consumer complaints, lawful accessibility tools, legally protected research or reports made through the security process.",
        ],
      },
    ],
    related: ["intellectual-property", "security", "terms"],
  },
  {
    slug: "intellectual-property",
    title: "Intellectual property & rights policy",
    summary: "Ownership, permitted use, third-party marks and a notice process for possible infringement.",
    highlight: "poodliving respects copyright, trademark, design and other rights and does not authorise infringing use of this storefront.",
    sections: [
      {
        title: "Store materials",
        paragraphs: [
          `The ${brand} name, original site copy, layout elements, product presentation and commissioned assets are protected to the extent provided by applicable law. Rights may belong to ${brand} or the relevant licensor.`,
          "No ownership transfers merely because material is publicly viewable.",
        ],
      },
      {
        title: "Permitted personal use",
        paragraphs: [
          "You may view pages, save order records and print a reasonable copy for personal shopping, support or record-keeping. You may link to a public page in a fair and non-deceptive way.",
          "You may not republish, sell, scrape, remove rights notices, create confusing imitations or use substantial site material for a competing commercial service without written permission or another lawful basis.",
        ],
      },
      {
        title: "Third-party rights",
        paragraphs: [
          "Third-party names, marks and product information belong to their respective owners. Their appearance identifies a product, compatibility or service and does not state that the owner sponsors or endorses this store.",
          "We expect suppliers and contributors to provide only material they are entitled to supply. We do not knowingly adopt another retailer’s protected text, photography, logo or trade dress as our own.",
        ],
      },
      {
        title: "Rights notice",
        paragraphs: [
          `A rights owner or authorised agent may email ${siteConfig.contact.email} with “Rights notice” in the subject. Identify the protected work or mark, the exact store URL or item, the complained-of use, your contact details and authority, and a good-faith statement that the information is accurate.`,
          "Do not send misleading notices. We may request clarification, temporarily limit disputed material, contact the supplier, or remove or correct content after a reasonable review.",
        ],
      },
      {
        title: "Response and restoration",
        paragraphs: [
          "A supplier or affected party may provide evidence of permission, ownership, lawful descriptive use or another defence. Material may be restored or revised when the concern is resolved.",
          "This voluntary process does not decide ownership and does not replace any procedure required by applicable law.",
        ],
      },
    ],
    related: ["legal-notice", "acceptable-use", "complaints"],
  },
  {
    slug: "accessibility",
    title: "Accessibility statement",
    summary: "Our approach to inclusive browsing, checkout support, known limitations and feedback.",
    highlight: "We aim for a storefront that can be navigated with a keyboard, zoomed, read by assistive technology and understood without colour alone.",
    sections: [
      {
        title: "Our commitment",
        paragraphs: [
          "We work toward widely recognised web-accessibility practices for navigation, readable text, form labels, focus visibility, alternative text and semantic page structure.",
          "Accessibility is an ongoing process and a statement of intent, not a claim that every page or third-party service is free from barriers.",
        ],
      },
      {
        title: "Supported use",
        paragraphs: [
          "The storefront is designed for current versions of major browsers and common assistive technologies. Layouts should reflow on smaller screens and at increased text or page zoom.",
          "Customers may use browser controls, screen readers, voice input and other lawful assistive tools. The acceptable-use policy does not prohibit ordinary assistive technology.",
        ],
      },
      {
        title: "Third-party and changing content",
        paragraphs: [
          "Hosted payment pages, carrier tracking and external links are controlled by their providers and can have different accessibility support. Product imagery and older content may not always convey every visual detail in alternative text.",
          "If a third-party step creates a barrier, contact us and we will try to provide the same information or a reasonable support route where possible.",
        ],
      },
      {
        title: "Report a barrier",
        paragraphs: [
          `Email ${siteConfig.contact.email} with “Accessibility” in the subject. Include the page or task, the barrier, your browser or assistive technology if you are comfortable sharing it, and the format or adjustment that would help.`,
          "We will acknowledge useful reports and prioritise issues that block product information, cart use, checkout or customer support.",
        ],
      },
    ],
    related: ["privacy", "acceptable-use", "complaints"],
  },
  {
    slug: "privacy-choices",
    title: "Privacy choices & data requests",
    summary: "A practical guide to access, correction, deletion, objection and browser-storage controls.",
    highlight: "GDPR rights apply to this Germany-based operation, subject to the conditions and exceptions for each right.",
    sections: [
      {
        title: "Available requests",
        paragraphs: [
          "Under the GDPR, you may ask what personal information is held, request a copy, correct inaccurate information, request deletion or restriction, object to certain uses, or request portability where the relevant conditions apply.",
          "You may also ask about the categories of providers that receive information and withdraw consent for future optional processing where consent is the basis used.",
        ],
      },
      {
        title: "Sale, sharing and marketing choices",
        paragraphs: [
          "This storefront does not state that it sells personal information or shares it for cross-context behavioural advertising. The checkout form does not automatically subscribe customers to marketing.",
          "If those practices change, the privacy and cookie notices and any legally required choice mechanism should be updated before the new use begins.",
        ],
      },
      {
        title: "Browser controls",
        paragraphs: [
          "Cart, wishlist and pending-order information can be removed through store controls or by clearing site data in the browser. Blocking essential storage may prevent core shopping or hosted-payment return features from working.",
          "Because the store does not describe behavioural advertising, a browser do-not-track signal does not change an advertising profile on this storefront.",
        ],
      },
      {
        title: "How to submit a request",
        paragraphs: [
          `Email ${siteConfig.contact.email} with “Privacy request” in the subject and describe the request, the country or region relevant to it, and any order or email address needed to locate the record.`,
          "We may verify identity proportionately and may refuse to disclose information that would expose another person, undermine security or conflict with a legal duty. An authorised agent may be asked for evidence of authority.",
        ],
      },
      {
        title: "Timing, appeals and complaints",
        paragraphs: [
          "We aim to acknowledge requests promptly and respond within any deadline that applies. Complex or repeated requests may require clarification or a lawful extension.",
          "If you disagree with the response, reply with the reason and ask for review. You may also complain to the Bayerisches Landesamt für Datenschutzaufsicht (BayLDA), the data-protection supervisory authority for private-sector organisations in Bavaria, or another competent supervisory authority.",
        ],
      },
    ],
    related: ["privacy", "cookies", "complaints"],
  },
  {
    slug: "security",
    title: "Security & responsible disclosure policy",
    summary: "Customer security guidance and a safe route to report a suspected vulnerability.",
    highlight: "Never email passwords, complete card numbers, wallet credentials, one-time codes or card security codes.",
    sections: [
      {
        title: "Customer security",
        paragraphs: [
          "Use a trusted device and network, check the page address before payment, keep software updated and be cautious of unexpected messages asking for payment or personal information.",
          `${brand} will not ask by email for a complete payment-card number, card security code, password or one-time authentication code.`,
        ],
      },
      {
        title: "Reporting a concern",
        paragraphs: [
          `Send suspected phishing, account misuse, data exposure or a technical security concern to ${siteConfig.contact.email} with “Security report” in the subject. Include a concise description, affected page, time observed and non-sensitive evidence.`,
          "Remove unrelated personal information from screenshots and do not send live credentials, payment data, malware or data belonging to another person.",
        ],
      },
      {
        title: "Research boundaries",
        paragraphs: [
          "This policy does not grant permission to test the live service. Do not access other users’ data, place deceptive orders, bypass payment, use social engineering, degrade availability, scan aggressively or exploit a vulnerability after demonstrating the minimum necessary evidence.",
          "Request written permission before any active security testing. Reports based only on non-intrusive observation are welcome.",
        ],
      },
      {
        title: "Our response",
        paragraphs: [
          "We will triage credible reports, preserve relevant information, involve service providers where needed and work toward proportionate remediation. We may ask for clarification and will avoid sharing a reporter’s identity unnecessarily.",
          "No bounty, payment, public credit or response deadline is promised unless agreed in writing before testing.",
        ],
      },
    ],
    related: ["privacy", "acceptable-use", "legal-notice"],
  },
  {
    slug: "responsible-sourcing",
    title: "Responsible sourcing policy",
    summary: "Supplier expectations for lawful work, human rights, product integrity and environmental care.",
    highlight: "We favour verifiable improvement and accurate information over unsupported environmental or ethical claims.",
    sections: [
      {
        title: "Supplier expectations",
        paragraphs: [
          "Suppliers are expected to comply with applicable labour, safety, environmental, anti-bribery, sanctions, product and trade laws and to maintain records appropriate to the goods supplied.",
          "Subcontracting should not be used to avoid these expectations.",
        ],
      },
      {
        title: "People and working conditions",
        paragraphs: [
          "We oppose forced labour, child labour, trafficking, unlawful discrimination, harassment and unsafe work. Workers should have lawful terms, pay, hours, freedom of movement and access to appropriate grievance channels.",
          "Age-appropriate learning or family work must never interfere with education or expose a child to hazardous activity.",
        ],
      },
      {
        title: "Materials and product integrity",
        paragraphs: [
          "Suppliers should provide accurate information about materials, origin, safety and required warnings and should not supply counterfeit, stolen or rights-infringing goods or content.",
          "Substances and packaging should comply with the rules applicable to the intended market, with traceability maintained where required.",
        ],
      },
      {
        title: "Environment and packaging",
        paragraphs: [
          "We encourage efficient material use, waste reduction, responsible forestry and mineral sourcing, recyclable packaging where practical, and lawful handling of chemicals, water and emissions.",
          "Terms such as recycled, biodegradable, non-toxic or sustainably sourced should be used only when they can be supported and properly qualified.",
        ],
      },
      {
        title: "Concerns and improvement",
        paragraphs: [
          `A concern about a product or supplier may be sent to ${siteConfig.contact.email}. We may seek evidence, require corrective action, pause purchasing or end a relationship depending on the seriousness and the supplier’s response.`,
          "This policy is an operational expectation, not a certification that every product has been independently audited.",
        ],
      },
    ],
    related: ["product-safety", "intellectual-property", "complaints"],
  },
  {
    slug: "complaints",
    title: "Complaints & dispute resolution policy",
    summary: "How to raise a concern, what information helps and how a complaint is reviewed and escalated.",
    highlight: "We aim to resolve concerns fairly and directly without limiting access to a bank, regulator, court or statutory dispute process.",
    sections: [
      {
        title: "How to complain",
        paragraphs: [
          `Email ${siteConfig.contact.email} with “Complaint” and the order reference in the subject where available. Explain what happened, the outcome you seek, relevant dates and any evidence needed to understand the issue.`,
          "Please avoid sending payment credentials, identity documents or unrelated sensitive information unless we request a secure method and the information is necessary.",
        ],
      },
      {
        title: "Review process",
        paragraphs: [
          "We will acknowledge the complaint, check the order and communications, request clarification if necessary, and provide a reasoned response. Urgent safety, security and accessibility concerns are prioritised.",
          "The time needed depends on complexity, delivery-provider input and whether an item requires inspection. We will provide an update if the review is materially delayed.",
        ],
      },
      {
        title: "Possible outcomes",
        paragraphs: [
          "An outcome may include information, correction, apology, delivery action, repair, replacement, refund, policy review or a decision that the requested remedy is not supported. Any mandatory remedy under applicable law takes priority.",
          "A refund or credit will be documented and processed through an appropriate method; customer-service staff should not ask for card security codes.",
        ],
      },
      {
        title: "Escalation",
        paragraphs: [
          "If you disagree with the first response, reply with the unresolved points and ask for a second review. A different person will review it where reasonably possible.",
          "You may also use an available consumer authority, privacy authority, payment dispute, mediation service or court. Nothing here requires arbitration or waives a non-excludable forum right unless a separate lawful agreement clearly says so.",
        ],
      },
      {
        title: "Fair treatment and records",
        paragraphs: [
          "We do not penalise a customer for making a good-faith complaint. Abusive, discriminatory or threatening communications can be limited while the underlying issue is still handled through a suitable channel.",
          "Complaint records are retained only as needed for resolution, fraud prevention, service improvement and legal obligations, consistent with the privacy policy.",
        ],
      },
    ],
    related: ["warranty-claims", "privacy-choices", "terms"],
  },
  {
    slug: "children",
    title: "Children’s privacy & purchasing policy",
    summary: "Rules for adult purchasing, children’s information and products intended for younger users.",
    highlight: "The store sells some children’s creative products, but checkout and customer accounts are intended for adults.",
    sections: [
      {
        title: "Adult purchasing",
        paragraphs: [
          "A person placing an order must be legally able to enter the purchase or act with the permission and supervision required by applicable law. Payment and delivery information should be supplied by an authorised adult.",
          "Age labels on products describe intended use or safety considerations; they do not change the age required to place an order.",
        ],
      },
      {
        title: "Children’s information",
        paragraphs: [
          "The store is not designed to collect personal information directly from a child. A child should not submit a contact form, delivery address or payment information without a parent or guardian.",
          "We do not knowingly use a child’s personal information for behavioural advertising.",
        ],
      },
      {
        title: "Parent or guardian requests",
        paragraphs: [
          `If you believe a child supplied personal information without appropriate permission, email ${siteConfig.contact.email} with “Child privacy” in the subject and enough detail to locate the information.`,
          "We may verify the requester’s identity and relationship before disclosing or deleting information, and may preserve limited records where required for safety, fraud prevention or law.",
        ],
      },
      {
        title: "Products for children",
        paragraphs: [
          "An adult should review product labels and supervise use according to the child’s age, ability and the activity. Small parts, sharp tools, caps, inks, paints and adhesives require particular care.",
          "Stop use and follow the product-safety reporting process if packaging is damaged, a warning is missing or a product appears unsuitable for its stated age group.",
        ],
      },
    ],
    related: ["privacy", "product-safety", "privacy-choices"],
  },
  {
    slug: "reviews-content",
    title: "Reviews & submitted content policy",
    summary: "Standards for genuine reviews, moderation, disclosure and rights in customer-submitted material.",
    highlight: "If review or content submission is offered, contributions must reflect a genuine experience and must not infringe another person’s rights.",
    sections: [
      {
        title: "Genuine contributions",
        paragraphs: [
          "A review should be based on actual experience with the relevant product or service. Do not submit fabricated ratings, coordinated manipulation, copied reviews or content written for an undisclosed reward.",
          "A reviewer who received a sample, discount or other material benefit must disclose it clearly.",
        ],
      },
      {
        title: "Content standards",
        paragraphs: [
          "Submitted content must not contain unlawful, threatening, discriminatory, sexually exploitative, deceptive, privacy-invasive, confidential or rights-infringing material. Do not publish another person’s address, contact details or payment information.",
          "Product criticism is allowed when relevant and expressed without harassment or unsupported accusations about an individual.",
        ],
      },
      {
        title: "Moderation and display",
        paragraphs: [
          "We may reject, remove, redact or decline to publish content that violates these standards, is unrelated, duplicates another submission or contains technical spam. We do not promise that every favourable or unfavourable review will be published.",
          "Moderation should not be used to create a misleading impression of customer opinion. Where a displayed rating is not based on verified customer submissions, it should not be labelled as verified.",
        ],
      },
      {
        title: "Permission to use submitted content",
        paragraphs: [
          "You retain ownership of original content you submit. By intentionally submitting it for publication, you grant us a non-exclusive, worldwide, royalty-free permission to host, reproduce, format and display it for operating and promoting the store, subject to applicable privacy law.",
          "You confirm that you have the necessary rights. This permission does not cover content sent only for a private support or warranty claim.",
        ],
      },
      {
        title: "Corrections and reports",
        paragraphs: [
          `Email ${siteConfig.contact.email} to request correction or removal of your review, report an undisclosed incentive, or identify infringing or unsafe content. Include the page and enough detail to locate the contribution.`,
          "Removal from public display may not erase a record that must be retained for fraud prevention, dispute resolution or legal compliance.",
        ],
      },
    ],
    related: ["intellectual-property", "acceptable-use", "privacy"],
  },
];

export const corePolicyLinks = {
  privacy: { label: "Privacy policy", to: "/privacy" },
  terms: { label: "Terms & conditions", to: "/terms" },
  cookies: { label: "Cookie policy", to: "/cookies" },
  shipping: { label: "Delivery policy", to: "/shipping" },
  returns: { label: "Returns & refunds", to: "/returns" },
};

export const policyLinks = policyPages.map(({ slug, title }) => ({
  label: title,
  to: `/policies/${slug}`,
  slug,
}));

export const getPolicyBySlug = (slug) => policyPages.find((policy) => policy.slug === slug);

export const getRelatedPolicy = (key) => {
  if (corePolicyLinks[key]) return corePolicyLinks[key];
  return policyLinks.find((policy) => policy.slug === key);
};
